import { FormSection } from '@/components'
import { PageHeader } from '@/components/PageHeader/PageHeader'
import {
  Alert,
  Box,
  Button,
  Container,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { type UserSettingsForm, UserSettingsFormSchema } from '@/features/user/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormField } from '@/components/Forms/FormField.tsx'
import { useSaveUserSettings } from '@/features/user/hooks/useSaveUserSettings.ts'
import { useState } from 'react'

export const Route = createFileRoute('/_authenticated/$companySlug/user-settings')({
  component: RouteComponent
})

function RouteComponent() {
  const { t } = useTranslation(['_routes', '_global', '@feature_user'])
  const { auth } = Route.useRouteContext()

  const [error, setError] = useState<Error>()
  const [saved, setSaved] = useState<boolean>(false)

  const saveUserSetting = useSaveUserSettings({
    onError: error => {
      setError(error)
      setSaved(false)
    }
  })

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<UserSettingsForm>({
    resolver: zodResolver(UserSettingsFormSchema),
    defaultValues: {
      language: auth.user.language
    }
  })

  async function onSubmit(data: UserSettingsForm) {
    await saveUserSetting.mutateAsync(data)
    setSaved(true)
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, marginLeft: 0 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <PageHeader subtitle={t('routes.user_settings.main.description')}>
          {t('routes.user_settings.main.title')}
        </PageHeader>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        {/* Success/Error Messages */}
        {saved && (
          <Alert severity="success" onClose={() => setSaved(false)}>
            {t('routes.user_settings.form.successMessage')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(undefined)}>
            {t(`_global:api.message.errors.${error.message}`, {
              defaultValue: error.cause as string
            })}
          </Alert>
        )}

        {/* User Role Section */}
        <FormSection
          title={t('routes.user_settings.main.sections.role.title')}
          description={t('routes.user_settings.main.sections.role.description')}
        >
          <FormField
            name="language"
            label={t('routes.user_settings.form.language.label')}
            control={control}
            errorTranslation={t}
            render={({ field, fieldState }) => (
              <Select {...field} displayEmpty error={!!fieldState.error}>
                {UserSettingsFormSchema.shape.language.options.map(lang => (
                  <MenuItem value={lang}>
                    <Stack direction="row" gap={1}>
                      <Stack direction="column" justifyContent="center">
                        <img
                          src={
                            'https://flagcdn.com/w20/' +
                            t(`routes.user_settings.form.language.${lang}.icon`) +
                            '.png'
                          }
                          srcSet={
                            'https://flagcdn.com/w40/' +
                            t(`routes.user_settings.form.language.${lang}.icon`) +
                            '.png 2x'
                          }
                          width="20"
                          height="13"
                          style={{ borderRadius: 2 }}
                        />
                      </Stack>
                      <Typography>
                        {t(`routes.user_settings.form.language.${lang}.label`)}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormSection>
        <Box sx={{ textAlign: 'right' }}>
          <Button type="submit" variant="contained" loading={isSubmitting}>
            {t('routes.user_settings.main.buttons.save')}
          </Button>
        </Box>
      </Box>
    </Container>
  )
}
